import API from "../../services/api";
import { getApiOrigin } from "../../utils/orgBranding";

const BASE = "/lifecycle";

const emptyToNull = (v) => (v === "" || v === undefined ? null : v);

/** Normalize onboarding payloads so empty date strings do not trigger 422 validation errors. */
export const sanitizeOnboardingPayload = (payload = {}) => ({
  ...payload,
  date_of_joining: emptyToNull(payload.date_of_joining),
  probation_end_date: emptyToNull(payload.probation_end_date),
});

/** Normalize offboarding payloads so empty date strings do not trigger 422 validation errors. */
export const sanitizeOffboardingPayload = (payload = {}) => ({
  ...payload,
  resignation_date: emptyToNull(payload.resignation_date),
  last_working_day: emptyToNull(payload.last_working_day),
  notice_period_start: emptyToNull(payload.notice_period_start),
  notice_period_end: emptyToNull(payload.notice_period_end),
});

/** Turn FastAPI/Pydantic error detail into a user-readable string. */
export const formatLifecycleError = (err, fallback = "Request failed") => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === "string" ? item : item?.msg || JSON.stringify(item)))
      .join("; ");
  }
  if (typeof detail === "object") return detail.message || detail.msg || JSON.stringify(detail);
  return fallback;
};

/** Employee self-service: own active onboarding / offboarding summary. */
export const getMyLifecycle = async () => {
  const { data } = await API.get(`${BASE}/me`);
  return data;
};

export const uploadMyOnboardingDocument = async (onboardingId, documentId, file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await API.post(
    `${BASE}/me/onboarding/${onboardingId}/documents/${documentId}/upload`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
};

export const getLifecycleDashboard = async () => {
  const { data } = await API.get(`${BASE}/dashboard`);
  return data;
};

export const listOnboardings = async (params = {}) => {
  const { data } = await API.get(`${BASE}/onboardings`, { params });
  return data;
};

export const getOnboarding = async (id) => {
  const { data } = await API.get(`${BASE}/onboardings/${id}`);
  return data;
};

export const createOnboarding = async (payload) => {
  const { data } = await API.post(`${BASE}/onboardings`, sanitizeOnboardingPayload(payload));
  return data;
};

export const updateOnboarding = async (id, payload) => {
  const { data } = await API.put(`${BASE}/onboardings/${id}`, sanitizeOnboardingPayload(payload));
  return data;
};

export const submitOnboarding = async (id) => {
  const { data } = await API.post(`${BASE}/onboardings/${id}/submit`);
  return data;
};

export const startOnboarding = async (id) => {
  const { data } = await API.post(`${BASE}/onboardings/${id}/start`);
  return data;
};

export const activateOnboarding = async (id) => {
  const { data } = await API.post(`${BASE}/onboardings/${id}/activate`);
  return data;
};

export const onboardingApprovalAction = async (onboardingId, approvalId, payload) => {
  const { data } = await API.post(
    `${BASE}/onboardings/${onboardingId}/approvals/${approvalId}/action`,
    payload
  );
  return data;
};

export const updateOnboardingTask = async (onboardingId, taskId, payload) => {
  const { data } = await API.patch(`${BASE}/onboardings/${onboardingId}/tasks/${taskId}`, payload);
  return data;
};

export const updateOnboardingDocument = async (onboardingId, documentId, payload) => {
  const { data } = await API.patch(
    `${BASE}/onboardings/${onboardingId}/documents/${documentId}`,
    payload
  );
  return data;
};

/** Upload a real file for an onboarding document (stored under /media/lifecycle/...). */
export const uploadOnboardingDocument = async (onboardingId, documentId, file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await API.post(
    `${BASE}/onboardings/${onboardingId}/documents/${documentId}/upload`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
};

/** Absolute URL to open/download a stored lifecycle document. */
export const getLifecycleDocumentUrl = (documentUrl) => {
  if (!documentUrl) return null;
  const raw = String(documentUrl).trim();
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  if (path.startsWith("/uploads/")) return null;
  return `${getApiOrigin()}${path}`;
};

/** Whether a clearance row counts as done (matches backend _clearance_complete). */
export const isClearanceComplete = (clearance) => {
  if (!clearance) return false;
  if (clearance.department === "Asset") {
    return ["Returned", "Damaged", "Missing"].includes(clearance.asset_status);
  }
  return clearance.status === "Cleared";
};

export const getClearanceProgress = (clearances = []) => {
  const total = clearances.length;
  const done = clearances.filter(isClearanceComplete).length;
  return { done, total };
};

export const getLifecycleDocumentName = (doc) => {
  if (!doc) return null;
  if (doc.original_filename) return doc.original_filename;
  if (!doc.document_url) return null;
  try {
    return decodeURIComponent(String(doc.document_url).split("/").pop() || "");
  } catch {
    return String(doc.document_url).split("/").pop() || null;
  }
};

export const listOffboardings = async (params = {}) => {
  const { data } = await API.get(`${BASE}/offboardings`, { params });
  return data;
};

export const getOffboarding = async (id) => {
  const { data } = await API.get(`${BASE}/offboardings/${id}`);
  return data;
};

export const createOffboarding = async (payload) => {
  const { data } = await API.post(`${BASE}/offboardings`, sanitizeOffboardingPayload(payload));
  return data;
};

export const updateOffboarding = async (id, payload) => {
  const { data } = await API.put(`${BASE}/offboardings/${id}`, sanitizeOffboardingPayload(payload));
  return data;
};

export const submitOffboarding = async (id) => {
  const { data } = await API.post(`${BASE}/offboardings/${id}/submit`);
  return data;
};

export const startClearance = async (id) => {
  const { data } = await API.post(`${BASE}/offboardings/${id}/clearance`);
  return data;
};

export const updateClearance = async (offboardingId, clearanceId, payload) => {
  const { data } = await API.patch(
    `${BASE}/offboardings/${offboardingId}/clearances/${clearanceId}`,
    payload
  );
  return data;
};

export const saveExitInterview = async (offboardingId, payload) => {
  const { data } = await API.post(`${BASE}/offboardings/${offboardingId}/exit-interview`, payload);
  return data;
};

export const calculateSettlement = async (offboardingId, payload) => {
  const { data } = await API.post(`${BASE}/offboardings/${offboardingId}/settlement`, payload);
  return data;
};

export const completeOffboarding = async (id) => {
  const { data } = await API.post(`${BASE}/offboardings/${id}/complete`);
  return data;
};

export const offboardingApprovalAction = async (offboardingId, approvalId, payload) => {
  const { data } = await API.post(
    `${BASE}/offboardings/${offboardingId}/approvals/${approvalId}/action`,
    payload
  );
  return data;
};

export const listTemplates = async (params = {}) => {
  const { data } = await API.get(`${BASE}/templates`, { params });
  return data;
};

export const createTemplate = async (payload) => {
  const { data } = await API.post(`${BASE}/templates`, payload);
  return data;
};

export const updateTemplate = async (id, payload) => {
  const { data } = await API.put(`${BASE}/templates/${id}`, payload);
  return data;
};

export const listAuditLogs = async (params = {}) => {
  const { data } = await API.get(`${BASE}/audit-logs`, { params });
  return data;
};

export const exportJoiningReport = async () => {
  const { data } = await API.get(`${BASE}/reports/onboarding/joining.csv`, { responseType: "blob" });
  return data;
};

export const exportExitReport = async () => {
  const { data } = await API.get(`${BASE}/reports/offboarding/exit.csv`, { responseType: "blob" });
  return data;
};

export const getLifecycleSettings = async () => {
  const { data } = await API.get(`${BASE}/settings`);
  return data;
};

export const updateLifecycleSettings = async (payload) => {
  const { data } = await API.put(`${BASE}/settings`, payload);
  return data;
};

export const autoCalculateSettlement = async (offboardingId) => {
  const { data } = await API.post(`${BASE}/offboardings/${offboardingId}/settlement/auto`);
  return data;
};

export const sendJoiningReminders = async () => {
  const { data } = await API.post(`${BASE}/reminders/joining`);
  return data;
};
